import axios from "axios";

// GeckoTerminal's public API is blocked/rate limited from browsers, so the same
// endpoints are served through CoinGecko's Pro on-chain API instead.
const COINGECKO_API_KEY = "CG-3cFZHArKNfDpmqA2hgAfFxgo";
const GECKOTERMINAL_BASE = "https://api.geckoterminal.com/api/v2/";
const COINGECKO_ONCHAIN_BASE = "https://pro-api.coingecko.com/api/v3/onchain/";

const CACHE_TTL_MS = 5 * 60 * 1000;
const STORAGE_PREFIX = "gecko:";
const MIN_GAP_MS = 250;
const MAX_RETRIES = 2;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const cache = new Map();
let queue = Promise.resolve();

const request = async (url) => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await axios.get(
        url.replace(GECKOTERMINAL_BASE, COINGECKO_ONCHAIN_BASE),
        { headers: { "x-cg-pro-api-key": COINGECKO_API_KEY } },
      );
    } catch (error) {
      if (error.response?.status !== 429 || attempt >= MAX_RETRIES) {
        throw error;
      }
      const retryAfter = Number(error.response.headers?.["retry-after"]);
      await sleep(retryAfter > 0 ? retryAfter * 1000 : 3000 * (attempt + 1));
    }
  }
};

const readStored = (url) => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_PREFIX + url));
  } catch (e) {
    return null;
  }
};

const writeStored = (url, data) => {
  try {
    localStorage.setItem(
      STORAGE_PREFIX + url,
      JSON.stringify({ time: Date.now(), data }),
    );
  } catch (e) {
    // storage full or unavailable; the in-memory cache still works
  }
};

// Accepts the old GeckoTerminal API URLs. Responses are cached for 5 minutes
// in memory and in localStorage (so page reloads don't refetch), identical
// in-flight calls are shared, and a stale stored response is used if the API
// call fails.
export const geckoGet = (url) => {
  const hit = cache.get(url);
  if (hit && Date.now() - hit.time < CACHE_TTL_MS) return hit.promise;

  const stored = readStored(url);
  if (stored && Date.now() - stored.time < CACHE_TTL_MS) {
    const promise = Promise.resolve({ data: stored.data });
    cache.set(url, { time: stored.time, promise });
    return promise;
  }

  const promise = queue
    .then(() => request(url))
    .then((res) => {
      writeStored(url, res.data);
      return res;
    })
    .catch((error) => {
      if (stored) return { data: stored.data };
      throw error;
    });
  queue = promise.catch(() => {}).then(() => sleep(MIN_GAP_MS));
  cache.set(url, { time: Date.now(), promise });
  promise.catch(() => cache.delete(url));
  return promise;
};
